import React, { useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { CascadingLocationSelectorProps } from './types';
import { useCascadingLocationSelector } from './hooks/useCascadingLocationSelector';
import { toAppLanguage } from "@/i18n";
import { getLocalizedLocationName } from "@/i18n/locations";
import SearchableDropdown from './components/SearchableDropdown';
import { isElementInPortal, validateSelections } from './utils/locationUtils';

const CascadingLocationSelector: React.FC<CascadingLocationSelectorProps> = (props) => {
  const { i18n, t } = useTranslation();
  const language = toAppLanguage(i18n.resolvedLanguage || i18n.language);
  const {
    selectedCountry = '',
    selectedState = '',
    selectedCity = '',
    labels = {
      country: t("location.country"),
      state: t("location.state"),
      city: t("location.city"),
    },
    showLabels = true, // Default to showing labels
    placeholders = {
      country: t("location.selectCountry"),
      state: t("location.selectState"),
      city: t("location.selectCity"),
    },
    required = {
      country: true,
      state: true,
      city: true
    },
    className = '',
    disabled = false,
    theme = 'light'
  } = props;

  const {
    // Data
    countries,
    states,
    cities,
    loading,
    error,
    
    // State
    isOpen,
    searchTerms,
    
    // Actions
    handleCountrySelect,
    handleStateSelect,
    handleCitySelect,
    toggleDropdown,
    updateSearchTerm,
    // closeAllDropdowns - unused, commenting out
  } = useCascadingLocationSelector(props);

  // Refs for click outside detection
  const countryRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Check if click is inside any portal dropdown content
      if (isElementInPortal(target)) {
        return; // Don't close if clicking inside dropdown
      }
      
      // Check each dropdown trigger individually
      if (countryRef.current && !countryRef.current.contains(event.target as Node)) {
        if (isOpen.country) {
          toggleDropdown('country');
          updateSearchTerm('country', '');
        }
      }
      if (stateRef.current && !stateRef.current.contains(event.target as Node)) {
        if (isOpen.state) {
          toggleDropdown('state');
          updateSearchTerm('state', '');
        }
      }
      if (cityRef.current && !cityRef.current.contains(event.target as Node)) {
        if (isOpen.city) {
          toggleDropdown('city');
          updateSearchTerm('city', '');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, toggleDropdown, updateSearchTerm]);

  // Validate current selections
  const validationErrors = validateSelections(
    selectedCountry,
    selectedState,
    selectedCity,
    required,
    {
      country: t("location.countryRequired"),
      state: t("location.stateRequired"),
      city: t("location.cityRequired"),
    }
  );

  // Check if there are any actual error messages (not just null values)
  const hasErrors = error && (error.countries || error.states || error.cities);

  if (hasErrors) {
    const errorMessages = [
      error.countries && t("location.countriesError", { message: error.countries }),
      error.states && t("location.statesError", { message: error.states }),
      error.cities && t("location.citiesError", { message: error.cities }),
    ].filter(Boolean);

    return (
      <div className={`p-4 border border-red-300 rounded-md bg-red-50 ${className}`}>
        <p className="text-red-700 text-sm">
          {t("location.loadError")}
        </p>
        <ul className="text-red-600 text-sm mt-1 list-disc list-inside">
          {errorMessages.map((msg, index) => (
            <li key={index}>{msg}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Country Selector */}
      <SearchableDropdown
        items={countries}
        selectedValue={selectedCountry}
        placeholder={placeholders.country!}
        searchTerm={searchTerms.country}
        isOpen={isOpen.country}
        loading={loading.countries}
        disabled={disabled}
        required={required.country!}
        theme={theme}
        onToggle={() => toggleDropdown('country')}
        onSearch={(term) => updateSearchTerm('country', term)}
        onSelect={handleCountrySelect}
        dropdownRef={countryRef}
        label={showLabels ? labels.country : undefined}
        error={validationErrors.country}
        getItemLabel={(country) =>
          getLocalizedLocationName(country.name, "countries", language)
        }
      />

      {/* State Selector */}
      <SearchableDropdown
        items={states}
        selectedValue={selectedState}
        placeholder={placeholders.state!}
        searchTerm={searchTerms.state}
        isOpen={isOpen.state}
        loading={loading.states}
        disabled={disabled || !selectedCountry}
        required={required.state!}
        theme={theme}
        onToggle={() => toggleDropdown('state')}
        onSearch={(term) => updateSearchTerm('state', term)}
        onSelect={handleStateSelect}
        dropdownRef={stateRef}
        label={showLabels ? labels.state : undefined}
        error={validationErrors.state}
        getItemLabel={(state) =>
          getLocalizedLocationName(state.name, "states", language)
        }
      />

      {/* City Selector */}
      <SearchableDropdown
        items={cities}
        selectedValue={selectedCity}
        placeholder={placeholders.city!}
        searchTerm={searchTerms.city}
        isOpen={isOpen.city}
        loading={loading.cities}
        disabled={disabled || !selectedState}
        required={required.city!}
        theme={theme}
        onToggle={() => toggleDropdown('city')}
        onSearch={(term) => updateSearchTerm('city', term)}
        onSelect={handleCitySelect}
        dropdownRef={cityRef}
        label={showLabels ? labels.city : undefined}
        error={validationErrors.city}
        getItemLabel={(city) =>
          getLocalizedLocationName(city.name, "cities", language)
        }
      />
    </div>
  );
};

export default CascadingLocationSelector;
